/**
 * DomainsAdmin (DC-1493)
 *
 * Settings/admin surface for the per-organization `domains` table that
 * powers the Triangulated FAIR Assessment (DC-1488 / DC-1492). Lists all
 * domains as cards with their dataset / interview / document contribution
 * counts; supports create + edit + delete via the existing `/api/domains`
 * REST endpoints shipped under DC-1490.
 *
 * Mounted under Settings at `/settings/domains` (DC-1645 — Domains is a
 * cross-cutting scope primitive, not a governance feature; the old
 * `/settings/governance/domains` path redirects here).
 *
 * Visual language inherits the teal/gold palette from the rest of the
 * governance settings surface; the count badges follow the inline-pill
 * styling pattern locked in by `triangulation/CoverageChip.tsx`.
 *
 * No new primitives — modals are inline divs with role=dialog. Slug
 * regex and field caps mirror the server-side Zod schema in
 * `server/routes/triangulationRoutes.ts`.
 */

import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { useApiClient } from '../../hooks/useApiClient';
import type { Domain, DomainSummary, DocumentedState } from '../../types/triangulation';

// ---------------------------------------------------------------------------
// Helpers
// ---------------------------------------------------------------------------

const SLUG_REGEX = /^[a-z0-9]+(-[a-z0-9]+)*$/;
const NAME_MAX = 255;
const SLUG_MAX = 255;
const DESC_MAX = 256;

/** Lowercase, replace runs of non-alphanumeric with single hyphen, trim. */
function slugify(input: string): string {
  return input
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, SLUG_MAX);
}

interface FormErrors {
  name?: string;
  slug?: string;
  description?: string;
  submit?: string;
}

function validateForm(values: { name: string; slug: string; description: string }): FormErrors {
  const errs: FormErrors = {};
  if (!values.name.trim()) errs.name = 'Name is required';
  else if (values.name.length > NAME_MAX) errs.name = `Name must be ${NAME_MAX} characters or fewer`;
  if (!values.slug.trim()) errs.slug = 'Slug is required';
  else if (values.slug.length > SLUG_MAX) errs.slug = `Slug must be ${SLUG_MAX} characters or fewer`;
  else if (!SLUG_REGEX.test(values.slug)) errs.slug = 'Slug may only contain lowercase letters, numbers, and hyphens';
  if (values.description.length > DESC_MAX) errs.description = `Description must be ${DESC_MAX} characters or fewer`;
  return errs;
}

// ---------------------------------------------------------------------------
// Sub-components
// ---------------------------------------------------------------------------

interface DomainCardProps {
  domain: DomainSummary;
  onEdit: () => void;
  onDelete: () => void;
  onToggleDocumentedState: (next: DocumentedState | null) => Promise<void>;
}

const COUNT_BADGE_STYLE: React.CSSProperties = {
  display: 'inline-flex',
  alignItems: 'center',
  gap: '0.25rem',
  padding: '0.125rem 0.5rem',
  borderRadius: 999,
  background: 'rgba(15, 110, 115, 0.08)',
  border: '1px solid #0F6E73',
  color: '#0F6E73',
  fontSize: '0.6875rem',
  fontWeight: 600,
  fontFamily: "'JetBrains Mono', Consolas, monospace",
};

function DomainCard({ domain, onEdit, onDelete, onToggleDocumentedState }: DomainCardProps) {
  const desc = domain.description ?? '';
  const truncated = desc.length > 120 ? `${desc.slice(0, 117)}…` : desc;
  const [togglePending, setTogglePending] = useState(false);
  const [toggleError, setToggleError] = useState<string | null>(null);
  const isDeliberatelyAbsent = domain.documented_state === 'deliberately_absent';

  const handleToggle = async () => {
    setTogglePending(true);
    setToggleError(null);
    try {
      await onToggleDocumentedState(isDeliberatelyAbsent ? null : 'deliberately_absent');
    } catch (err) {
      setToggleError(err instanceof Error ? err.message : String(err));
    } finally {
      setTogglePending(false);
    }
  };
  return (
    <div
      data-testid={`domain-card-${domain.slug}`}
      style={{
        background: '#ffffff',
        border: '1px solid #e5e7eb',
        borderRadius: '0.5rem',
        padding: '1rem 1.25rem',
        display: 'flex',
        flexDirection: 'column',
        gap: '0.625rem',
      }}
    >
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: '0.75rem' }}>
        <div style={{ minWidth: 0 }}>
          <h3 style={{ margin: 0, fontSize: '1rem', fontWeight: 600, color: '#111827', overflow: 'hidden', textOverflow: 'ellipsis' }}>
            {domain.name}
          </h3>
          <code
            style={{
              display: 'inline-block',
              marginTop: '0.125rem',
              fontSize: '0.75rem',
              color: '#6b7280',
              fontFamily: "'JetBrains Mono', Consolas, monospace",
            }}
          >
            {domain.slug}
          </code>
        </div>
        <div style={{ display: 'flex', gap: '0.375rem', flexShrink: 0 }}>
          <button
            type="button"
            onClick={onEdit}
            data-testid={`edit-domain-${domain.slug}`}
            style={buttonStyle('secondary')}
          >
            Edit
          </button>
          <button
            type="button"
            onClick={onDelete}
            data-testid={`delete-domain-${domain.slug}`}
            style={buttonStyle('danger-outline')}
          >
            Delete
          </button>
        </div>
      </div>

      {desc && (
        <p
          title={desc.length > 120 ? desc : undefined}
          style={{ margin: 0, fontSize: '0.8125rem', color: '#4b5563', lineHeight: 1.4 }}
        >
          {truncated}
        </p>
      )}

      <div style={{ display: 'flex', gap: '0.5rem', flexWrap: 'wrap' }}>
        <span style={COUNT_BADGE_STYLE} title={`${domain.dataset_count} datasets assigned to this domain`}>
          {domain.dataset_count} datasets
        </span>
        <span style={COUNT_BADGE_STYLE} title={`${domain.interview_count} interviews assigned to this domain`}>
          {domain.interview_count} interviews
        </span>
        <span style={COUNT_BADGE_STYLE} title={`${domain.document_count} governance documents assigned to this domain`}>
          {domain.document_count} documents
        </span>
      </div>

      {/* DC-1605: Governed-leg state toggle. When deliberately_absent is asserted,
          computeDocumentedLeg short-circuits and emits `state: deliberately_absent`
          across all four pillars rather than treating absence as `not_yet_gathered`. */}
      <div
        style={{
          marginTop: '0.25rem',
          paddingTop: '0.625rem',
          borderTop: '1px dashed #e5e7eb',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'space-between',
          gap: '0.75rem',
          flexWrap: 'wrap',
        }}
      >
        <div style={{ minWidth: 0 }}>
          <div style={{ fontSize: '0.75rem', fontWeight: 600, color: '#374151' }}>Governed-leg state</div>
          <div
            data-testid={`domain-documented-state-${domain.slug}`}
            style={{
              fontSize: '0.75rem',
              color: isDeliberatelyAbsent ? '#9A5F00' : '#6b7280',
              fontStyle: isDeliberatelyAbsent ? 'normal' : 'italic',
            }}
          >
            {isDeliberatelyAbsent
              ? 'Deliberately absent — consultant has confirmed no documented signal.'
              : 'Derived from contributing documents.'}
          </div>
          {toggleError && (
            <p role="alert" style={{ margin: '0.25rem 0 0 0', fontSize: '0.6875rem', color: '#b91c1c' }}>
              {toggleError}
            </p>
          )}
        </div>
        <button
          type="button"
          onClick={handleToggle}
          disabled={togglePending}
          data-testid={`toggle-documented-state-${domain.slug}`}
          style={buttonStyle('secondary')}
          title={
            isDeliberatelyAbsent
              ? 'Clear the deliberately_absent assertion; revert to derived state from contributing docs.'
              : 'Assert that this domain has no documented signal. Use when there is no governance documentation to find — distinct from documentation that simply has not been gathered yet.'
          }
        >
          {togglePending
            ? 'Saving…'
            : isDeliberatelyAbsent
              ? 'Clear assertion'
              : 'Mark deliberately absent'}
        </button>
      </div>
    </div>
  );
}

interface DomainFormModalProps {
  mode: 'create' | 'edit';
  initial: Domain | null;
  onClose: () => void;
  onSubmit: (values: { name: string; slug: string; description: string | null }) => Promise<void>;
}

function DomainFormModal({ mode, initial, onClose, onSubmit }: DomainFormModalProps) {
  const [name, setName] = useState(initial?.name ?? '');
  const [slug, setSlug] = useState(initial?.slug ?? '');
  // Track whether the user has manually edited slug so autogen doesn't clobber.
  const [slugTouched, setSlugTouched] = useState(Boolean(initial?.slug));
  const [description, setDescription] = useState(initial?.description ?? '');
  const [errors, setErrors] = useState<FormErrors>({});
  const [submitting, setSubmitting] = useState(false);

  const handleNameChange = (next: string) => {
    setName(next);
    if (!slugTouched) {
      setSlug(slugify(next));
    }
  };

  const handleSlugChange = (next: string) => {
    setSlug(next);
    setSlugTouched(true);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    const trimmed = { name: name.trim(), slug: slug.trim(), description: description.trim() };
    const validation = validateForm(trimmed);
    if (Object.keys(validation).length > 0) {
      setErrors(validation);
      return;
    }
    setErrors({});
    setSubmitting(true);
    try {
      await onSubmit({
        name: trimmed.name,
        slug: trimmed.slug,
        description: trimmed.description.length > 0 ? trimmed.description : null,
      });
    } catch (err) {
      const msg = err instanceof Error ? err.message : String(err);
      // Surface 409 conflict copy specifically; other errors get the raw msg.
      setErrors({
        submit: msg.toLowerCase().includes('409') || msg.toLowerCase().includes('conflict')
          ? 'A domain with that name or slug already exists in this organization.'
          : msg,
      });
    } finally {
      setSubmitting(false);
    }
  };

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="domain-form-title" style={modalBackdropStyle} data-testid="domain-form-modal">
      <div style={modalCardStyle}>
        <h2 id="domain-form-title" style={{ margin: '0 0 1rem 0', fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>
          {mode === 'create' ? 'Create domain' : `Edit ${initial?.name ?? 'domain'}`}
        </h2>
        <form onSubmit={handleSubmit} noValidate>
          <FieldLabel htmlFor="domain-name">Name</FieldLabel>
          <input
            id="domain-name"
            data-testid="domain-name-input"
            value={name}
            onChange={(e) => handleNameChange(e.target.value)}
            maxLength={NAME_MAX}
            disabled={submitting}
            style={inputStyle(Boolean(errors.name))}
          />
          {errors.name && <FieldError>{errors.name}</FieldError>}

          <FieldLabel htmlFor="domain-slug">Slug</FieldLabel>
          <input
            id="domain-slug"
            data-testid="domain-slug-input"
            value={slug}
            onChange={(e) => handleSlugChange(e.target.value)}
            maxLength={SLUG_MAX}
            disabled={submitting}
            style={{ ...inputStyle(Boolean(errors.slug)), fontFamily: "'JetBrains Mono', Consolas, monospace" }}
          />
          <p style={helpTextStyle}>Lowercase letters, numbers, and hyphens. Used in URLs and PDF anchors.</p>
          {errors.slug && <FieldError>{errors.slug}</FieldError>}

          <FieldLabel htmlFor="domain-description">Description</FieldLabel>
          <textarea
            id="domain-description"
            data-testid="domain-description-input"
            value={description}
            onChange={(e) => setDescription(e.target.value)}
            maxLength={DESC_MAX}
            disabled={submitting}
            rows={3}
            style={inputStyle(Boolean(errors.description))}
          />
          <p style={helpTextStyle}>Optional. {DESC_MAX - description.length} characters remaining.</p>
          {errors.description && <FieldError>{errors.description}</FieldError>}

          {errors.submit && (
            <div
              role="alert"
              data-testid="domain-form-submit-error"
              style={{
                marginTop: '0.75rem',
                padding: '0.5rem 0.75rem',
                background: '#fef2f2',
                border: '1px solid #fca5a5',
                borderRadius: '0.375rem',
                color: '#991b1b',
                fontSize: '0.8125rem',
              }}
            >
              {errors.submit}
            </div>
          )}

          <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem', marginTop: '1.25rem' }}>
            <button type="button" onClick={onClose} disabled={submitting} style={buttonStyle('secondary')}>
              Cancel
            </button>
            <button type="submit" disabled={submitting} data-testid="domain-form-submit" style={buttonStyle('primary')}>
              {submitting ? 'Saving...' : mode === 'create' ? 'Create domain' : 'Save changes'}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
}

interface DeleteConfirmModalProps {
  domain: DomainSummary;
  onCancel: () => void;
  onConfirm: () => Promise<void>;
}

function DeleteConfirmModal({ domain, onCancel, onConfirm }: DeleteConfirmModalProps) {
  const [submitting, setSubmitting] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const handleConfirm = async () => {
    setSubmitting(true);
    setError(null);
    try {
      await onConfirm();
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setSubmitting(false);
    }
  };

  const total = domain.dataset_count + domain.interview_count + domain.document_count;

  return (
    <div role="dialog" aria-modal="true" aria-labelledby="domain-delete-title" style={modalBackdropStyle} data-testid="domain-delete-modal">
      <div style={modalCardStyle}>
        <h2 id="domain-delete-title" style={{ margin: '0 0 0.75rem 0', fontSize: '1.125rem', fontWeight: 600, color: '#111827' }}>
          Delete {domain.name}?
        </h2>
        <p style={{ margin: '0 0 0.75rem 0', fontSize: '0.875rem', color: '#374151', lineHeight: 1.5 }}>
          {total === 0
            ? 'This domain has no assigned artifacts. Deletion is reversible only by re-creating the domain manually.'
            : `This domain currently has ${domain.dataset_count} dataset(s), ${domain.interview_count} interview(s), and ${domain.document_count} governance document(s) assigned. They will be unassigned (their domain_id becomes NULL) — the artifacts themselves are not deleted.`}
        </p>
        {error && (
          <div role="alert" style={{ marginBottom: '0.75rem', padding: '0.5rem 0.75rem', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '0.375rem', color: '#991b1b', fontSize: '0.8125rem' }}>
            {error}
          </div>
        )}
        <div style={{ display: 'flex', justifyContent: 'flex-end', gap: '0.5rem' }}>
          <button type="button" onClick={onCancel} disabled={submitting} style={buttonStyle('secondary')}>
            Cancel
          </button>
          <button type="button" onClick={handleConfirm} disabled={submitting} data-testid="domain-delete-confirm" style={buttonStyle('danger')}>
            {submitting ? 'Deleting...' : 'Delete domain'}
          </button>
        </div>
      </div>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Main component
// ---------------------------------------------------------------------------

export const DomainsAdmin: React.FC = () => {
  // Stable refs so the loaders below stay referentially stable across renders.
  const { get, post, patch, delete: del, invalidateCache } = useApiClient();

  const [domains, setDomains] = useState<DomainSummary[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [query, setQuery] = useState('');

  const [formMode, setFormMode] = useState<'create' | 'edit' | null>(null);
  const [editTarget, setEditTarget] = useState<Domain | null>(null);
  const [deleteTarget, setDeleteTarget] = useState<DomainSummary | null>(null);

  const refresh = useCallback(async () => {
    setLoading(true);
    setError(null);
    try {
      invalidateCache('/api/triangulation/domains');
      const list = await get<DomainSummary[]>('/api/triangulation/domains');
      setDomains(Array.isArray(list) ? list : []);
    } catch (err) {
      setError(err instanceof Error ? err.message : String(err));
    } finally {
      setLoading(false);
    }
  }, [get, invalidateCache]);

  useEffect(() => {
    void refresh();
  }, [refresh]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return domains;
    return domains.filter(
      (d) => d.name.toLowerCase().includes(q) || d.slug.toLowerCase().includes(q),
    );
  }, [domains, query]);

  const handleCreate = useCallback(
    async (values: { name: string; slug: string; description: string | null }) => {
      await post('/api/domains', values);
      setFormMode(null);
      setEditTarget(null);
      await refresh();
    },
    [post, refresh],
  );

  const handleUpdate = useCallback(
    async (values: { name: string; slug: string; description: string | null }) => {
      if (!editTarget) return;
      await patch(`/api/domains/${editTarget.id}`, values);
      setFormMode(null);
      setEditTarget(null);
      await refresh();
    },
    [editTarget, patch, refresh],
  );

  const handleDelete = useCallback(async () => {
    if (!deleteTarget) return;
    await del(`/api/domains/${deleteTarget.id}`);
    setDeleteTarget(null);
    await refresh();
  }, [del, deleteTarget, refresh]);

  // DC-1605: PATCH /api/domains/:id/documented-state — consultant assertion of
  // the documented-leg state. Body shape: { documented_state: 'deliberately_absent' | null }.
  const handleToggleDocumentedState = useCallback(
    async (id: string, next: DocumentedState | null) => {
      await patch(`/api/domains/${id}/documented-state`, { documented_state: next });
      await refresh();
    },
    [patch, refresh],
  );

  return (
    <div data-testid="domains-admin" style={{ padding: '1.5rem 1.75rem', maxWidth: 1080 }}>
      <header style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '1rem', gap: '1rem' }}>
        <div>
          <h1 style={{ margin: 0, fontSize: '1.5rem', fontWeight: 700, color: '#111827' }}>Domains</h1>
          <p style={{ margin: '0.25rem 0 0 0', fontSize: '0.875rem', color: '#6b7280' }}>
            Organizational data domains that power the Triangulated FAIR Assessment.
            Assign datasets, interviews, and governance documents to a domain to compare the
            Technical, Perceived, and Governed legs side by side.
          </p>
        </div>
        <button
          type="button"
          onClick={() => {
            setEditTarget(null);
            setFormMode('create');
          }}
          data-testid="create-domain-button"
          style={buttonStyle('primary')}
        >
          + Create domain
        </button>
      </header>

      <div style={{ marginBottom: '1rem' }}>
        <input
          type="search"
          placeholder="Filter by name or slug…"
          value={query}
          onChange={(e) => setQuery(e.target.value)}
          data-testid="domains-search"
          style={{
            ...inputStyle(false),
            maxWidth: 320,
          }}
        />
      </div>

      {loading && (
        <div data-testid="domains-loading" style={{ padding: '2rem', textAlign: 'center', color: '#6b7280' }}>
          Loading domains…
        </div>
      )}

      {!loading && error && (
        <div data-testid="domains-error" role="alert" style={{ padding: '1rem 1.25rem', background: '#fef2f2', border: '1px solid #fca5a5', borderRadius: '0.5rem', color: '#991b1b' }}>
          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.875rem' }}>Failed to load domains: {error}</p>
          <button type="button" onClick={() => void refresh()} style={buttonStyle('secondary')}>
            Retry
          </button>
        </div>
      )}

      {!loading && !error && domains.length === 0 && (
        <div data-testid="domains-empty" style={{ padding: '2.5rem 1.5rem', textAlign: 'center', background: '#f9fafb', border: '1px dashed #d1d5db', borderRadius: '0.5rem', color: '#6b7280' }}>
          <p style={{ margin: '0 0 0.5rem 0', fontSize: '0.9375rem', color: '#374151' }}>
            No domains yet
          </p>
          <p style={{ margin: 0, fontSize: '0.8125rem' }}>
            Create your first to start assigning datasets, interviews, and governance documents to organizational areas.
          </p>
        </div>
      )}

      {!loading && !error && domains.length > 0 && filtered.length === 0 && (
        <div style={{ padding: '1.5rem', textAlign: 'center', color: '#6b7280', fontSize: '0.875rem' }}>
          No domains match &quot;{query}&quot;.
        </div>
      )}

      {!loading && !error && filtered.length > 0 && (
        <div
          data-testid="domains-list"
          style={{
            display: 'grid',
            gridTemplateColumns: 'repeat(auto-fill, minmax(320px, 1fr))',
            gap: '0.875rem',
          }}
        >
          {filtered.map((d) => (
            <DomainCard
              key={d.id}
              domain={d}
              onEdit={() => {
                setEditTarget(d);
                setFormMode('edit');
              }}
              onDelete={() => setDeleteTarget(d)}
              onToggleDocumentedState={(next) => handleToggleDocumentedState(d.id, next)}
            />
          ))}
        </div>
      )}

      {formMode && (
        <DomainFormModal
          mode={formMode}
          initial={editTarget}
          onClose={() => {
            setFormMode(null);
            setEditTarget(null);
          }}
          onSubmit={formMode === 'create' ? handleCreate : handleUpdate}
        />
      )}

      {deleteTarget && (
        <DeleteConfirmModal
          domain={deleteTarget}
          onCancel={() => setDeleteTarget(null)}
          onConfirm={handleDelete}
        />
      )}
    </div>
  );
};

export default DomainsAdmin;

// ---------------------------------------------------------------------------
// Style helpers (inline; no new CSS files to maintain)
// ---------------------------------------------------------------------------

type ButtonVariant = 'primary' | 'secondary' | 'danger' | 'danger-outline';

function buttonStyle(variant: ButtonVariant): React.CSSProperties {
  const base: React.CSSProperties = {
    padding: '0.4375rem 0.875rem',
    fontSize: '0.8125rem',
    fontWeight: 600,
    borderRadius: '0.375rem',
    cursor: 'pointer',
    border: '1px solid transparent',
    transition: 'background 0.15s ease, color 0.15s ease, border-color 0.15s ease',
  };
  if (variant === 'primary') {
    return { ...base, background: '#0F6E73', color: '#ffffff' };
  }
  if (variant === 'secondary') {
    return { ...base, background: '#ffffff', color: '#374151', borderColor: '#d1d5db' };
  }
  if (variant === 'danger') {
    return { ...base, background: '#dc2626', color: '#ffffff' };
  }
  // danger-outline
  return { ...base, background: '#ffffff', color: '#b91c1c', borderColor: '#fca5a5' };
}

function inputStyle(invalid: boolean): React.CSSProperties {
  return {
    width: '100%',
    padding: '0.5rem 0.75rem',
    fontSize: '0.875rem',
    border: `1px solid ${invalid ? '#dc2626' : '#d1d5db'}`,
    borderRadius: '0.375rem',
    background: '#ffffff',
    color: '#111827',
    boxSizing: 'border-box',
  };
}

const helpTextStyle: React.CSSProperties = {
  margin: '0.25rem 0 0 0',
  fontSize: '0.75rem',
  color: '#6b7280',
};

const modalBackdropStyle: React.CSSProperties = {
  position: 'fixed',
  inset: 0,
  background: 'rgba(17, 24, 39, 0.55)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
  zIndex: 1000,
  padding: '1rem',
};

const modalCardStyle: React.CSSProperties = {
  background: '#ffffff',
  borderRadius: '0.5rem',
  padding: '1.25rem 1.5rem',
  width: '100%',
  maxWidth: 480,
  boxShadow: '0 20px 40px rgba(0, 0, 0, 0.25)',
  maxHeight: '90vh',
  overflowY: 'auto',
};

function FieldLabel({ htmlFor, children }: { htmlFor: string; children: React.ReactNode }) {
  return (
    <label
      htmlFor={htmlFor}
      style={{
        display: 'block',
        fontSize: '0.8125rem',
        fontWeight: 600,
        color: '#374151',
        marginTop: '0.875rem',
        marginBottom: '0.25rem',
      }}
    >
      {children}
    </label>
  );
}

function FieldError({ children }: { children: React.ReactNode }) {
  return (
    <p role="alert" style={{ margin: '0.25rem 0 0 0', fontSize: '0.75rem', color: '#b91c1c' }}>
      {children}
    </p>
  );
}
